export interface StorageWrapper {
  get: <T>(key: string, defaultValue?: T) => T | undefined;
  set: <T>(key: string, value: T) => void;
  remove: (key: string) => void;
}

interface StorageWrapperOptions {
  serialize?: <T>(value: T) => string;
  deserialize?: <T>(value: string) => T;
}

const defaultSerialize = <T>(value: T): string => {
  return JSON.stringify({ value });
};

const defaultDeserialize = <T>(value: string): T => {
  const item = JSON.parse(value) as { value: T };
  return item.value;
};

const createEmptyStorageWrapper = (): StorageWrapper => {
  return {
    get: <T>(key: string, defaultValue?: T): T | undefined => {
      return defaultValue;
    },
    set: <T>(key: string, value: T) => {
      // do nothing
    },
    remove: (key: string) => {
      // do nothing
    }
  };
};

export const createStorageWrapper = (
  getStorage: () => Storage | undefined,
  options: StorageWrapperOptions = {}
): StorageWrapper => {
  const storage = (() => {
    try {
      return getStorage();
    } catch (e) {
      return undefined;
    }
  })();

  if (!storage) {
    return createEmptyStorageWrapper();
  }

  const serialize = options.serialize ?? defaultSerialize;
  const deserialize = options.deserialize ?? defaultDeserialize;

  return {
    get: <T>(key: string, defaultValue?: T): T | undefined => {
      try {
        const value: string | null = storage.getItem(key);
        if (!value) {
          return defaultValue;
        }
        return deserialize<T>(value);
      } catch (e) {
        return defaultValue;
      }
    },
    set: <T>(key: string, value: T) => {
      try {
        storage.setItem(key, serialize(value));
      } catch (e) {
        // do nothing
      }
    },
    remove: (key: string) => {
      try {
        storage.removeItem(key);
      } catch (e) {
        // do nothing
      }
    }
  };
};
